import React, { useState } from 'react';
import { Alert, Stack } from '@mui/material';
import GenericInfoModal from '../../../components/GenericInfoModal';
import GenericTextField from '../../../components/GenericTextField/GenericTextField';
import { MESSAGE_TYPE } from '../../../common/constants/common.constants';
import { createNotification } from '../../../utilities/helper/notification.helper';
import { cancelInvoice } from '../../../api/invoicing.api';
import { useTranslation } from 'react-i18next';

interface CancelInvoiceModalProps {
  kindergartenId: string;
  invoiceId: string;
  invoiceNumber: string | null;
  onClose: () => void;
  onCancelled: () => void;
}

/**
 * Cancelling an issued invoice, with the reason the admin actually has.
 *
 * The reason used to be hardcoded to "Cancelled from the admin panel", which is not a
 * reason - it is where the button was. It is kept on the invoice, so the one person who
 * ever reads it is whoever has to explain the cancellation months later.
 */
const CancelInvoiceModal: React.FC<CancelInvoiceModalProps> = ({
  kindergartenId,
  invoiceId,
  invoiceNumber,
  onClose,
  onCancelled
}) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');

  const handleConfirm = async () => {
    if (!reason.trim()) {
      createNotification(MESSAGE_TYPE.ERROR, t('invoices.cancel.reasonRequired'));
      return;
    }
    try {
      await cancelInvoice(kindergartenId, invoiceId, reason.trim());
      createNotification(MESSAGE_TYPE.SUCCESS, t('invoices.cancel.done'));
      onCancelled();
      onClose();
    } catch (error: any) {
      createNotification(MESSAGE_TYPE.ERROR, error?.response?.data?.message ?? t('invoices.cancel.failed'));
    }
  };

  return (
    <GenericInfoModal
      open
      onClose={onClose}
      onConfirm={handleConfirm}
      label={t('invoices.cancel.title')}
      confirmLabel={t('invoices.cancel.title')}
      closeLabel={t('invoices.cancel.keep')}
    >
      <Stack spacing={2} sx={{ mt: 1 }}>
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          {invoiceNumber
            ? t('invoices.cancel.warningNumbered', { number: invoiceNumber })
            : t('invoices.cancel.warning')}
        </Alert>
        <GenericTextField
          label={t('invoices.cancel.reason')}
          required
          value={reason}
          onChange={e => setReason(e.target.value)}
          helperText={t('invoices.cancel.reasonHelp')}
        />
      </Stack>
    </GenericInfoModal>
  );
};

export default CancelInvoiceModal;
